import { z } from 'zod';
import { Locale, UserKind, UserStatus } from '../enums.js';

const slug = z
  .string()
  .min(2)
  .max(40)
  .regex(/^[a-z0-9][a-z0-9-]*$/, 'slug must be lowercase letters, digits and dashes');

export const StaffLoginSchema = z.object({
  tenantSlug: slug.optional(),
  email: z.string().email().max(120).transform((s) => s.trim().toLowerCase()),
  password: z.string().min(8).max(128),
});
export type StaffLogin = z.infer<typeof StaffLoginSchema>;

export const SuperLoginSchema = z.object({
  email: z.string().email().max(120).transform((s) => s.trim().toLowerCase()),
  password: z.string().min(8).max(128),
});
export type SuperLogin = z.infer<typeof SuperLoginSchema>;

export const OtpRequestSchema = z.object({
  tenantSlug: slug.optional(),
  /** Any Egyptian mobile format; normalized server-side to E.164. */
  phone: z.string().min(8).max(20),
});
export type OtpRequest = z.infer<typeof OtpRequestSchema>;

export const OtpVerifySchema = OtpRequestSchema.extend({
  code: z.string().regex(/^\d{6}$/),
  deviceInfo: z.string().max(200).optional(),
});
export type OtpVerify = z.infer<typeof OtpVerifySchema>;

export const RefreshSchema = z.object({ refreshToken: z.string().min(32).max(256) });
export type Refresh = z.infer<typeof RefreshSchema>;

export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(8).max(128),
  newPassword: z.string().min(8).max(128),
});
export type ChangePassword = z.infer<typeof ChangePasswordSchema>;

/** Self-service profile update (driver app account screen / staff preferences). */
export const UpdateMeSchema = z.object({
  fullName: z.string().trim().min(2).max(120).optional(),
  locale: z.enum(Locale).optional(),
});
export type UpdateMe = z.infer<typeof UpdateMeSchema>;

export const FcmTokenSchema = z.object({
  token: z.string().min(20).max(512),
  platform: z.enum(['android', 'ios', 'web']),
});
export type FcmTokenInput = z.infer<typeof FcmTokenSchema>;

export interface TokenPair {
  accessToken: string;
  /** Seconds until access token expiry. */
  expiresIn: number;
  refreshToken: string;
  refreshExpiresAt: string;
}

export interface SessionUser {
  id: string;
  kind: UserKind;
  email: string | null;
  phone: string | null;
  fullName: string;
  locale: Locale;
  status: UserStatus;
  role: { id: string; code: string; nameAr: string; nameEn: string; permissions: string[] };
  driverId: string | null;
}

export interface SessionTenant {
  id: string;
  slug: string;
  nameAr: string;
  nameEn: string | null;
  timezone: string;
  locale: Locale;
  currency: string;
  logoFileId: string | null;
  plan: { code: string; nameAr: string; nameEn: string; maxVehicles: number } | null;
  vehicleCount: number;
}

export interface LoginResponse extends TokenPair {
  realm: 'tenant';
  user: SessionUser;
  tenant: SessionTenant;
}

export interface SuperLoginResponse extends TokenPair {
  realm: 'super';
  admin: { id: string; email: string; name: string };
}

/** Returned when the email/phone exists in more than one tenant. */
export interface TenantChoice {
  slug: string;
  nameAr: string;
  nameEn: string | null;
}
